import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { TemplateRef } from '@angular/core';
import { Params } from '@angular/router';
import { DimensionType } from '@app/logs/bid-adjustment/bid-adjustment.models';
import { environment } from '@env/environment';
import { NzDrawerOptions } from 'ng-zorro-antd/drawer';
import { ModalOptions } from 'ng-zorro-antd/modal';
import { KeyOf } from '../utilities/typescript.utilities';
import { ApiAccessLevel, ApiEndpoint } from './api.models';
import { Currency } from './customer.models';
import {
  PG_GOAL_METRIC_CLICKS_PER_MONTH,
  PG_GOAL_METRIC_CONVERSIONS_PER_MONTH,
  PG_GOAL_METRIC_COST_PER_CLICK,
  PG_GOAL_METRIC_COST_PER_CONVERSION,
  PG_GOAL_METRIC_COST_PER_DAY,
  PG_GOAL_METRIC_COST_PER_MONTH,
  PG_GOAL_METRIC_CRR,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PG_GOAL_METRIC_ROAS,
  PG_GOAL_METRIC_ROI,
} from './metrics.constants';

export type AdPlatform = 'GOOGLE' | 'AMAZON' | 'MICROSOFT' | 'EBAY' | 'WALMART';
export type ActiveAdPlatform = AdPlatform;
export type Layout = 'MOBILE' | 'DESKTOP';
export type Micros = number;
export type Stage = 'local' | 'development' | 'production' | 'beta' | 'staging' | 'testsuite';

export enum AdPlatformType {
  Google = 'GOOGLE',
  Amazon = 'AMAZON',
  Microsoft = 'MICROSOFT',
  Ebay = 'EBAY',
  Walmart = 'WALMART',
  Facebook = 'FACEBOOK',
  ITD = 'ITD',
  TTD = 'TTD',
}

export type AdPlatformAccessStatus =
  | 'OK'
  | 'READ-ONLY'
  | 'ACCOUNT-DISABLED'
  | 'PERMISSION-DENIED-ACCOUNT'
  | 'PERMISSION-DENIED-MANAGER'
  | 'OAUTH-REVOKED-ACCOUNT'
  | 'OAUTH-REVOKED-MANAGER'
  | 'POLICY-NOT-ENROLLED';

export type Status = 'success' | 'warning' | 'danger';

export const PlatformImages: Record<ActiveAdPlatform, string> = {
  GOOGLE: `${environment.app.cdnURL}assets/signup/Google.png`,
  AMAZON: `${environment.app.cdnURL}assets/signup/Amazon.png`,
  MICROSOFT: `${environment.app.cdnURL}assets/signup/Microsoft.png`,
  EBAY: `${environment.app.cdnURL}assets/signup/Ebay.png`,
  WALMART: `${environment.app.cdnURL}assets/signup/Walmart.png`,
};

export interface HasAccountPlatform {
  platform?: AdPlatform;
  accountPlatform?: AdPlatform;
  platformAccessStatus?: AdPlatformAccessStatus;
  optimize?: boolean;
}

export interface AccountDimension {
  dimension: DimensionType;
  optimized: boolean;
}

export interface Account {
  accountId: string;
  adspertId: string;
  adspertAccountId: number;
  accountName: string;
  connectedAt: Date;
  currency: Currency;
  optimizationStart: Date;
  platform: AdPlatform;
  accountPlatform?: AdPlatform;
  platformAccessStatus?: AdPlatformAccessStatus;
  customerId?: number;
  customerName?: string;
  mainCustomerId?: number;
  amzHasMWS?: boolean;
  amzAccountType?: 'SELLER' | 'VENDOR';
  amzRegion?: AMZRegions;
  amzMarketplace?: any;
  tags?: any;
  optimize?: boolean;
  signupCompleted?: boolean;
  setupCompleted?: boolean;
  disconnected?: boolean;
  snoozed?: boolean;
  customerChangeTarget?: number;
  customerChangeStarted?: boolean;
  dimensions?: AccountDimension[];
}

export type AMZRegions = null | 'EU' | 'FE' | 'NA';

export enum MetricType {
  currency = 'currency',
  number = 'number',
  percent = 'percent',
}

export type ComponentStates = null | 'processing' | 'noData' | 'error' | 'success';
export interface Toast {
  show: boolean;
  title: string;
  duration?: number;
  action?: () => unknown;
  actionTxt?: string;
  type: 'success' | 'info' | 'warning' | 'error' | 'blank' | string;
}

export type GoalType =
  | typeof PG_GOAL_METRIC_COST_PER_DAY
  | typeof PG_GOAL_METRIC_COST_PER_CLICK
  | typeof PG_GOAL_METRIC_COST_PER_CONVERSION
  | typeof PG_GOAL_METRIC_CRR
  | typeof PG_GOAL_METRIC_ROI
  | typeof PG_GOAL_METRIC_ROAS
  | typeof PG_GOAL_METRIC_PROFIT_MAXIMIZATION;

export type Goal = {
  goalCurrency: Currency | null;
  goalType: GoalType;
  goalValue: Micros | number;
  goalMicros?: Micros | number;
  goalRatio?: number;
};

export type GoalPayload = {
  goal_currency: Currency | null;
  goal_type: GoalType;
  goal_value: Micros | number;
};

export interface GoalMetrics {
  [PG_GOAL_METRIC_COST_PER_DAY]: number;
  [PG_GOAL_METRIC_COST_PER_CLICK]: number;
  [PG_GOAL_METRIC_COST_PER_CONVERSION]: number;
  [PG_GOAL_METRIC_CRR]: number;
  [PG_GOAL_METRIC_ROI]: number;
  [PG_GOAL_METRIC_ROAS]: number;
  [PG_GOAL_METRIC_PROFIT_MAXIMIZATION]: number;
  [PG_GOAL_METRIC_CLICKS_PER_MONTH]?: number;
  [PG_GOAL_METRIC_CONVERSIONS_PER_MONTH]?: number;
  [PG_GOAL_METRIC_COST_PER_MONTH]?: number;
}

export interface GoalMetricsWithDate extends GoalMetrics {
  date: string;
}
export type WarningPriority = 'HIGH-PRIORITY' | 'MEDIUM-PRIORITY' | 'LOW-PRIORITY' | 'ACKNOWLEDGED' | 'NO-WARNINGS';
export type WarningType =
  | 'ACCOUNT-MOVED'
  | 'ACCOUNT-DISCONNECTED'
  | 'ACCOUNT-ARCHIVED'
  | 'ACCOUNT-API-ACCESS-OK'
  | 'ACCOUNT-API-ACCESS-READ-ONLY'
  | 'ACCOUNT-API-ACCESS-NONE'
  | 'CAMPAIGN-ADDED-ACCOUNT-DEFAULT'
  | 'CAMPAIGN-ADDED-AMAZON-MANUAL'
  | 'CAMPAIGN-DELETED'
  | 'CAMPAIGN-PAUSED'
  | 'CAMPAIGN-ACTIVATED'
  | 'CAMPAIGN-OUT-OF-BUDGET'
  | 'CAMPAIGN-UNSUPPORTED'
  | 'CAMPAIGN-INVALID-OPTIMIZATION-TYPE'
  | 'GOAL-UNREACHABLE-FIRST'
  | 'GOAL-UNREACHABLE-SECOND'
  | 'LEGACY-INVALID-GOAL-TYPE'
  | 'LEGACY-INVALID-CAMPAIGN-OPTIMIZATION-TYPE'
  | 'NOT-ENOUGH-CONVERSIONS'
  | 'GOAL-DEVIATES-FROM-HISTORICAL'
  | 'DAILY-COST-TARGET-TOO-LOW'
  | 'UNSUITABLE-BIDDING-STRATEGY'
  | 'BUDGET-TOO-LOW'
  | 'BUDGET-TOO-HIGH'
  | 'USER-MAX-BID-TOO-LOW'
  | 'USER-MIN-BID-TOO-HIGH';

export interface Warning {
  type: WarningType;
  priority: 'HIGH' | 'LOW' | 'MEDIUM' | string;
  adspertAccountId: number;
  adspertCampaignId?: string;
  campaignId: number;
  createdAt: Date;
  campaigns?: [number] | string;
  acknowledgedAt: Date;
}

export interface UserMaxBid {
  currency: Currency;
  value: number;
  micros?: number;
}

export interface UserMaxBidAPIResponse {
  currency: Currency;
  micros: Micros;
}

export interface CampaignBudgetForGoals {
  currency: Currency;
  value: number;
}

export interface PerformanceGroup {
  name: string;
  performanceGroupId: number;
  performanceGroupName?: string;
  pgCurrency?: Currency;
  accountName?: string;
  accountPlatform?: AdPlatform;
  campaigns?: number[];
  defaultForAccounts?: number[];
  createdAt?: Date;
  date?: Date;
  enabled?: boolean;
  goalTypes?: GoalType[];
  goals?: Goal[];
  optimizationType?: CampaignOptimizationValue;
  warningStatus?: WarningPriority;
  warnings?: Warning[];
  userMinBid?: UserMaxBid;
  userMaxBid?: UserMaxBid;
}
export interface Portfolio {
  name: string;
  id: string;
}
export type CampaignType =
  | 'AMAZON-SPONSORED-BRANDS'
  | 'AMAZON-SPONSORED-DISPLAY'
  | 'AMAZON-SPONSORED-PRODUCTS'
  | 'EBAY-PROMOTED-LISTINGS-ADVANCED'
  | 'EBAY-PROMOTED-LISTINGS-STANDARD'
  | 'GOOGLE-DISPLAY'
  | 'GOOGLE-EXPRESS'
  | 'GOOGLE-SEARCH'
  | 'GOOGLE-SHOPPING'
  | 'GOOGLE-UNIVERSAL-APP'
  | 'GOOGLE-UNKNOWN'
  | 'GOOGLE-VIDEO'
  | 'MICROSOFT-AUDIENCE'
  | 'MICROSOFT-DYNAMIC-SEARCH-ADS'
  | 'MICROSOFT-SEARCH'
  | 'MICROSOFT-SHOPPING'
  | 'WALMART-SPONSORED-BRANDS'
  | 'WALMART-SPONSORED-PRODUCTS'
  | 'WALMART-SPONSORED-VIDEO';
export type CampaignOptimizationSupport =
  | 'SUPPORTED'
  | 'UNSUPPORTED-ADW-BID-STRATEGY'
  | 'UNSUPPORTED-ADW-PAYMENT-MODE'
  | 'UNSUPPORTED-BNG-BID-STRATEGY'
  | 'UNSUPPORTED-CAMPAIGN-TYPE'
  | 'UNSUPPORTED-EBY-MARKETPLACE'
  | 'UNSUPPORTED-VCPM';

export enum CampaignStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
  PAUSED = 'PAUSED',
}

export interface Campaign {
  accountCurrency: Currency;
  accountId: string;
  accountIsDemo: boolean;
  accountName: string;
  accountOptimize: boolean;
  accountPlatform: AdPlatform;
  accountTagsUser: string[];
  adspertAccountId: number;
  adspertCampaignId: string;
  adspertPortfolioId: string;
  amzPortfolioId: string;
  amzPortfolioName: string;
  campaignId: string;
  campaignLabels: string[];
  campaignName: string;
  campaignOptimizationSupport: CampaignOptimizationSupport;
  campaignOptimizationType: CampaignOptimizationValue;
  campaignOptimize: boolean;
  campaignStatus: CampaignStatus;
  campaignType: CampaignType;
  campaignAmzTargetingType: CampaignTargetingType;
  performanceGroupEnabled: boolean;
  performanceGroupGoalTypes: GoalType[];
  performanceGroupGoals: Goal[];
  performanceGroupId: number;
  performanceGroupName: string;
  performanceGroupOptimizationType: CampaignOptimizationValue;
}

export interface AdGroups {
  name: string;
  id: string;
  cmpgnId: string;
  status: AdgroupStatus | string;
}

export enum AdgroupStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
  PAUSED = 'PAUSED',
}

export interface Criteria {
  name: string;
  criterionName?: string;
  id: string;
  cmpgnId: string;
  adGrpId: string;
  adGrpName: string;
  status: CampaignStatus | string;
}

export interface Keywords {
  name: string;
  criterionType: 'KEYWORD' | 'PAT' | string;
  matchType: 'BROAD' | 'EXACT' | 'PHRASE' | string;
  conversionShare: number;
}

export interface FilterOption {
  text: string;
  value: string;
  byDefault?: boolean;
}

export interface TableColumnConfig<T extends object = any> {
  key: KeyOf<T> | string;
  displayName?: string;
  align?: 'left' | 'right' | 'center';
  width: number | string;
  excludeInURI?: boolean;
  hasTemplate?: boolean;
  tooltip?: string;
  sortable?: boolean;
  defaultSort?: number | null;
  filterable?: boolean;
  radioFilter?: boolean;
  setFilter?: boolean;
  filterOptions?: FilterOption[] | Observable<FilterOption[]>;
  filterValues?: any;
  tourStep?: string;
  tourStepOrder?: number;
  template?: TemplateRef<any> | string | ((item: T) => string | TemplateRef<any>);
  headerTemplate?: TemplateRef<any>;
}

interface TableRefiner {
  key: string;
  value: any;
}

export interface ReusableTableConfiguration<T extends object = any> {
  endpoint: string;
  query?: string;
  pageSize?: 10 | 25 | 50 | 100 | 500;
  height?: number;
  width?: number;
  title?: string;
  titleHeader?: 'h2' | 'h3';
  defaultRefiners?: TableRefiner[];
  includeExtraData?: KeyOf<T>[];
  actionsColumnWidth?: number;
  singleSort?: boolean;
  columns: TableColumnConfig<T>[];
  search?: {
    keys: KeyOf<T>[];
    defaultQuery: string;
    placeholder?: string;
  };
  sortTransformer?: (data: TableRefiner[]) => TableRefiner[];
  onSelectionChanged?: (data: T) => void;
  onExpandChanged?: (data: T) => void;
  onSearchChanged?: (data: T) => void;
  onData?: (data: T[]) => void;
  camelTransform?: boolean;
  accessLevel?: ApiAccessLevel;
}

export enum AccountProvider {
  Amazon = 'AMAZON',
  Google = 'GOOGLE',
  Microsoft = 'MICROSOFT',
  Ebay = 'EBAY',
  Walmart = 'WALMART',
}

export const TABLE_PLATFORM_FILTER = [
  { text: 'Amazon', value: AccountProvider.Amazon },
  { text: 'eBay', value: AccountProvider.Ebay },
  { text: 'Google', value: AccountProvider.Google },
  { text: 'Microsoft', value: AccountProvider.Microsoft },
  { text: 'Walmart', value: AccountProvider.Walmart },
];

export const TABLE_ENABLED_DISABLED_FILTER = (tl: TranslateService): Observable<FilterOption[]> => {
  return combineLatest([tl.get('global-filter.enabled'), tl.get('global-filter.disabled')]).pipe(
    map(([enabled, disabled]) => [
      { text: enabled, value: true.toString() },
      { text: disabled, value: false.toString() },
    ]),
  );
};

export enum CompareValues {
  LE = 'LE',
  GE = 'GE',
  EQUALS = 'EQ',
  RANGE = 'RANGE',
}

export type SubFilterOption = { active: boolean; selected?: boolean; text?: string; key: string; platform?: any };
export type SubFilterTypes = 'multi-select' | 'single-select' | 'query' | 'queryMultiple' | 'switch' | 'compare';

export interface SubFilter {
  type: SubFilterTypes;
  key: string;
  columnName: string;
  displayName?: string;
  apiRefinerType: 'filter' | 'search';
  active?: boolean;
  options?: SubFilterOption[];
  allOptions?: SubFilterOption[];
  optionsUri?: { api: ApiEndpoint; route: string };
  maxSelection?: number;
  value?: string | boolean | null;
  isAdded?: boolean;
  subType?: MetricType;
  currency?: Currency;
  queryKeyword?: 'contains' | 'equals';
  optiTypes?: CampaignOptimizationValue[];
  on?: boolean;
  off?: boolean;
  comparator?: CompareValues;
  autocomplete?: boolean;
  customTemplate?: boolean;
  enabledComparators?: CompareValues[];
  disabled?: boolean;
}

export enum CampaignTargetingType {
  Auto = 'AUTO',
  Manual = 'MANUAL',
}
export const CampaignOptimizationType = {
  CLICK: 'CLICK',
  REVENUE: 'REVENUE',
  PROFIT: 'PROFIT',
  VCPM: 'VCPM',
} as const;

export type CampaignOptimizationValue = (typeof CampaignOptimizationType)[keyof typeof CampaignOptimizationType];

export type CampaignEbayMarketplaceIds =
  | 'EBAY_AT'
  | 'EBAY_AU'
  | 'EBAY_BE'
  | 'EBAY_CA'
  | 'EBAY_CH'
  | 'EBAY_DE'
  | 'EBAY_ES'
  | 'EBAY_FR'
  | 'EBAY_GB'
  | 'EBAY_HK'
  | 'EBAY_IE'
  | 'EBAY_IN'
  | 'EBAY_IT'
  | 'EBAY_MY'
  | 'EBAY_NL'
  | 'EBAY_PH'
  | 'EBAY_PL'
  | 'EBAY_SG'
  | 'EBAY_TH'
  | 'EBAY_TW'
  | 'EBAY_US'
  | 'EBAY_VN'
  | 'EBAY_MOTORS_US';

export enum BudgetType {
  Daily = 'DAILY',
  Both = 'BOTH',
  AllTime = 'ALL_TIME',
}

export interface CustomerSummary {
  accountPlatforms?: AdPlatform[];
  accountCurrencies: string[];
  campaignTypes: CampaignType[];
  campaignOptimizationTypes: CampaignOptimizationValue[];
  campaignProfitSupported: boolean;
  campaignRevenueSupported: boolean;
  campaignEbyMarketplaceIds: CampaignEbayMarketplaceIds[];
  accountCount: number;
  campaignCount: number;
  hasAmzRasCampaigns: boolean;
}

export const REFINER_ACCOUNT = 'ACCOUNT';
export const REFINER_PG = 'PG';
export const REFINER_CAMPAIGN = 'CAMPAIGN';
export const REFINER_ADGROUP = 'ADGROUP';
export const REFINER_CRITERIA = 'CRITERIA';
export const REFINER_SEARCH = 'SEARCH';
export const REFINER_DATE = 'DATE';

export enum GlobalFilterType {
  ACCOUNTS = 'account',
  PG = 'pg',
  CAMPAIGN = 'campaign',
  ADGROUP = 'adgroup',
  CRITERIA = 'criteria',
}

export type CompositeId = `${number}:${number}`;

export const SourceRefinersMapping = {
  adgroupLabels: 'sourceAdgroupLabels',
  adgroupName: 'sourceAdgroupName',
  adgroupStatus: 'sourceAdgroupStatus',
  adspertAdgroupId: 'sourceAdspertAdgroupId',
  adspertCampaignId: 'sourceAdspertCampaignId',
  adspertPortfolioId: 'sourceAdspertPortfolioId',
  amzPortfolioName: 'sourceAmzPortfolioName',
  campaignEbyMarketplaceId: 'sourceCampaignEbyMarketplaceId',
  campaignLabels: 'sourceCampaignLabels',
  campaignName: 'sourceCampaignName',
  campaignOptimizationSupport: 'sourceCampaignOptimizationSupport',
  campaignOptimizationType: 'sourceCampaignOptimizationType',
  campaignOptimize: 'sourceCampaignOptimize',
  campaignStatus: 'sourceCampaignStatus',
  campaignType: 'sourceCampaignType',
  performanceGroupEnabled: 'sourcePerformanceGroupEnabled',
  performanceGroupGoalTypes: 'sourcePerformanceGroupGoalTypes',
  performanceGroupId: 'sourcePerformanceGroupId',
  performanceGroupName: 'sourcePerformanceGroupName',
  performanceGroupOptimizationType: 'sourcePerformanceGroupOptimizationType',
};

export interface DateRanges {
  range: [Date, Date];
  compare?: [Date, Date] | null;
}
export interface SortMenuOption {
  key: string;
  isAsc: boolean;
}

export interface SortPayload {
  key: string;
  isAsc: boolean;
  isMetric?: boolean;
}

export type APIFilterValuesForState = {
  apiFilters: Params;
  apiSearch: Params;
  filters: [string, string][];
  param: string;
};
export const NonDismissablePaneConfig = {
  nzMask: false,
  nzClosable: false,
  nzCloseOnNavigation: false,
  nzMaskClosable: false,
  nzKeyboard: false,
};

export const FullscreenPaneConfig: Partial<NzDrawerOptions> = {
  nzWidth: '100%',
  nzPlacement: 'right',
  ...NonDismissablePaneConfig,
  nzWrapClassName: 'a-bg-color-active adspt-no-padding btn-close-right',
};

export const FullScreenNonDismissableModalConfig: Partial<ModalOptions> = {
  nzWidth: '100vw',
  nzMask: false,
  nzClosable: false,
  nzCloseOnNavigation: false,
  nzMaskClosable: false,
  nzKeyboard: false,
  nzClassName: 'a-modal-full-screen',
  nzAutofocus: null,
  nzWrapClassName: 'a-modal-full-screen-wrap',
};

// 2025-08-21
export const NeedsCustomerInformationDate = 1755734400000;
